import { Globe, Smartphone, Bot, HelpCircle, Clock } from 'lucide-react';

/**
 * Платформы, с которых юзер попадает в продукт. Значения приходят из
 * `X-Huddle-Client` на бэкенде (см. lib/client-source.ts).
 *
 * `legacy` — не платформа, а аккаунты, зарегистрированные до введения учёта:
 * у них источник NULL и восстановить его нельзя. Держим отдельно от `unknown`,
 * чтобы не выдавать «не смогли определить» за «не знали тогда».
 */
export type Platform = 'web' | 'ios' | 'android' | 'bot' | 'unknown' | 'legacy';

const META: Record<Platform, { label: string; icon: typeof Globe; className: string }> = {
  web: { label: 'Сайт', icon: Globe, className: 'text-sky-400' },
  ios: { label: 'iOS', icon: Smartphone, className: 'text-zinc-200' },
  android: { label: 'Android', icon: Smartphone, className: 'text-emerald-400' },
  bot: { label: 'Бот', icon: Bot, className: 'text-violet-400' },
  unknown: { label: 'Не определён', icon: HelpCircle, className: 'text-zinc-500' },
  legacy: { label: 'До учёта', icon: Clock, className: 'text-zinc-600' },
};

export function platformMeta(p: string) {
  return META[p as Platform] ?? META.unknown;
}

export const PLATFORM_ORDER: Platform[] = ['ios', 'android', 'web', 'bot', 'unknown', 'legacy'];

/** Фильтруемые платформы — без `legacy`, он не источник, а отсутствие данных. */
export const FILTERABLE_PLATFORMS: Platform[] = ['web', 'ios', 'android', 'bot', 'unknown'];

export function PlatformBadge({ platform, muted }: { platform: string; muted?: boolean }) {
  const { label, icon: Icon, className } = platformMeta(platform);
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] ${muted ? 'text-zinc-500' : className}`}
      title={label}
    >
      <Icon className="h-3 w-3 shrink-0" />
      {label}
    </span>
  );
}

/**
 * Список платформ, с которых юзер заходил. Первая — самая свежая (бэкенд
 * сортирует по last_seen_at), поэтому она и показывается ярко.
 */
export function PlatformList({ platforms }: { platforms: string[] | null }) {
  if (!platforms?.length) return <span className="text-zinc-600">—</span>;
  return (
    <span className="inline-flex items-center gap-2">
      {platforms.map((p, i) => (
        <PlatformBadge key={p} platform={p} muted={i > 0} />
      ))}
    </span>
  );
}
